// src/components/AgentDevReportDialog.jsx
//
// NEW — shows the development report the agent has written for the user.
// Opened from the "View my development report" button in
// AgentDevProfilePanel. Sits above the panel (z-index 1550; panel is 1500,
// panel Select menus are 1600).
//
// Props: open, onClose, uid, agentId, agentLabel

import React, { Component } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon       from '@material-ui/icons/Close';
import RefreshIcon     from '@material-ui/icons/Refresh';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { callM172, describeError } from '../utils/m172Client';

const ACCENT = '#1a6fba';
const BG     = '#eef4fb';

const styles = theme => ({
  dialogTitle: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    paddingBottom:  0,
  },
  content: {
    background: BG,
  },
  sheet: {
    background:   '#fff',
    borderRadius: 8,
    padding:      theme.spacing(3, 3.5),
    maxWidth:     640,
    margin:       '0 auto',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2),
    },
  },
  reportTitle: {
    fontSize:   22,
    fontWeight: 600,
    lineHeight: 1.3,
    color:      '#0f2f52',
    marginBottom: theme.spacing(0.5),
  },
  meta: {
    fontSize:     12,
    color:        theme.palette.text.secondary,
    marginBottom: theme.spacing(2.5),
  },
  opening: {
    fontSize:     16,
    lineHeight:   1.65,
    color:        theme.palette.text.primary,
    borderLeft:   `3px solid ${ACCENT}`,
    paddingLeft:  theme.spacing(1.75),
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(2.75),
  },
  sectionHeading: {
    fontSize:     16,
    fontWeight:   600,
    color:        ACCENT,
    marginBottom: theme.spacing(0.75),
  },
  paragraph: {
    fontSize:     15,
    lineHeight:   1.7,
    color:        theme.palette.text.primary,
    marginBottom: theme.spacing(1.25),
    whiteSpace:   'pre-line',
  },
  closing: {
    fontSize:   15,
    lineHeight: 1.7,
    fontStyle:  'italic',
    color:      '#0f2f52',
    marginTop:  theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop:  `1px solid ${theme.palette.divider}`,
  },
  signature: {
    fontSize:  13,
    color:     theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  centred: {
    textAlign: 'center',
    padding:   theme.spacing(6, 2),
    color:     theme.palette.text.secondary,
  },
  actions: {
    justifyContent: 'space-between',
    padding:        theme.spacing(1, 2),
  },
});

function formatDate(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function reportAsText(report, agentLabel, generatedAt) {
  const lines = [report.title, `${agentLabel} — ${formatDate(generatedAt)}`, ''];
  if (report.opening) lines.push(report.opening, '');
  report.sections.forEach(s => lines.push(s.heading, s.body, ''));
  if (report.closing) lines.push(report.closing);
  return lines.join('\n');
}

class AgentDevReportDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading:       false,
      refreshing:    false,
      report:        null,
      generatedAt:   null,
      nextRefreshAt: null,
      noProfile:     false,
      error:         null,
      copied:        false,
    };
  }

  componentDidMount() {
    if (this.props.open) this.loadReport(false);
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) this.loadReport(false);
  }

  async loadReport(refresh) {
    const { uid, agentId } = this.props;
    this.setState({
      [refresh ? 'refreshing' : 'loading']: true,
      error: null, noProfile: false, copied: false,
    });

    try {
      const res = await callM172({
        uid, agentId,
        operation: 'report',
        extra:     refresh ? { refresh: true } : {},
        timeout:   110000,
      });
      const data = res.data || {};

      if (!data.success) {
        this.setState({
          loading: false, refreshing: false,
          error: data.error || `Could not load your report (HTTP ${res.status}).`,
        });
        return;
      }
      if (!data.report) {
        this.setState({ loading: false, refreshing: false, noProfile: data.reason === 'no_profile', report: null });
        return;
      }

      this.setState({
        loading:       false,
        refreshing:    false,
        report:        data.report,
        generatedAt:   data.generatedAt,
        nextRefreshAt: data.nextRefreshAt || null,
      });
    } catch (e) {
      this.setState({ loading: false, refreshing: false, error: describeError(e) });
    }
  }

  async copyReport() {
    const { report, generatedAt } = this.state;
    if (!report) return;
    try {
      await navigator.clipboard.writeText(reportAsText(report, this.props.agentLabel, generatedAt));
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch (e) {
      this.setState({ error: 'Copy is not available in this browser.' });
    }
  }

  renderBody() {
    const { classes, agentLabel } = this.props;
    const { loading, report, generatedAt, noProfile, error } = this.state;

    if (loading) {
      return (
        <div className={classes.centred}>
          <CircularProgress size={28} style={{ color: ACCENT, marginBottom: 16 }} />
          <Typography variant="body2">
            {agentLabel} is writing your report. This can take up to a minute.
          </Typography>
        </div>
      );
    }

    if (error && !report) {
      return (
        <div className={classes.centred}>
          <Typography color="error" variant="body2">{error}</Typography>
        </div>
      );
    }

    if (noProfile || !report) {
      return (
        <div className={classes.centred}>
          <Typography variant="body2">
            There isn't enough to report on yet. Keep talking with {agentLabel} and your report will be ready after your next session.
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.sheet}>
        <Typography component="h2" className={classes.reportTitle}>{report.title}</Typography>
        <Typography className={classes.meta}>
          Written by {agentLabel} on {formatDate(generatedAt)}
        </Typography>

        {report.opening && (
          <Typography className={classes.opening}>{report.opening}</Typography>
        )}

        {report.sections.map((s, i) => (
          <div key={i} className={classes.section}>
            <Typography component="h3" className={classes.sectionHeading}>{s.heading}</Typography>
            {s.body.split(/\n\s*\n/).map((p, j) => (
              <Typography key={j} className={classes.paragraph}>{p}</Typography>
            ))}
          </div>
        ))}

        {report.closing && (
          <>
            <Typography className={classes.closing}>{report.closing}</Typography>
            <Typography className={classes.signature}>{agentLabel}</Typography>
          </>
        )}

        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 12 }}>{error}</Typography>
        )}
      </div>
    );
  }

  render() {
    const { open, onClose, classes, agentLabel } = this.props;
    const { report, loading, refreshing, nextRefreshAt, copied } = this.state;

    const canRefresh  = !!report && !loading && !refreshing
      && (!nextRefreshAt || Date.now() >= nextRefreshAt);
    const refreshHint = report && nextRefreshAt && Date.now() < nextRefreshAt
      ? `You can ask for a new report after ${new Date(nextRefreshAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. It also updates automatically after your next session.`
      : 'Ask your agent to rewrite the report from your latest conversations.';

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        style={{ zIndex: 1550 }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            {agentLabel} — Your Development Report
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close report">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className={classes.content}>
          {this.renderBody()}
        </DialogContent>

        <DialogActions className={classes.actions}>
          <div>
            <Tooltip title={refreshHint}>
              <span>
                <Button
                  size="small"
                  startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
                  onClick={() => this.loadReport(true)}
                  disabled={!canRefresh}
                  style={{ color: canRefresh ? ACCENT : undefined }}
                >
                  {refreshing ? 'Rewriting…' : 'Rewrite report'}
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              startIcon={<FileCopyOutlinedIcon />}
              onClick={() => this.copyReport()}
              disabled={!report || loading}
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </Button>
          </div>
          <Button onClick={onClose} size="small">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default withStyles(styles)(AgentDevReportDialog);